/**
 * KEIKKA Homepage v3 — map-first white, conversion-focused.
 *
 * Structure (top → bottom):
 *   1. Hero (left: headline + subtitle + verified badge + 2 CTAs;
 *            right: map teaser with 3 stats)
 *   2. Quick search bar (category + location + "Näita kaardil")
 *   3. Popular category strip (6 cards)
 *   4. Why Keikka (3 trust bullets)
 *   5. Compact "How it works" (3 one-liners)
 *   6. Short provider CTA
 *   7. Minimal footer
 *
 * Above-the-fold (1 & 2) must fit a 1366x768 viewport without scrolling.
 */
import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '@/lib/context';
import { usePrefs } from '@/lib/usePrefs';
import { GlobalNav } from '@/components/GlobalNav';
import { LocationDetectedBanner } from '@/components/LocationDetectedBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Truck, Wrench, Home as HomeIcon, Package, Building2, MapPin,
  ArrowRight, ShieldCheck, FileText, Bell, CheckCircle, PlayCircle, Plus,
} from 'lucide-react';
import trackMapEvent from '@/lib/mapAnalytics';

const API = process.env.REACT_APP_BACKEND_URL;

// ---- live-demo helpers --------------------------------------------------
// Best-effort country detection: explicit user pref > navigator.language
// (e.g. nb-NO, en-NO) > IP via /api/geo/detect > 'NO' fallback.
const COUNTRY_FROM_LANG = {
  nb: 'NO', no: 'NO', nn: 'NO',
  fi: 'FI', et: 'EE', sv: 'SE', da: 'DK',
  pl: 'PL', de: 'DE', ru: 'EE', en: 'NO',
};

function _norwegianBrowser() {
  try {
    const langs = (navigator.languages || [navigator.language || '']).map(
      (s) => (s || '').toLowerCase(),
    );
    return langs.some((l) => l.startsWith('nb') || l.startsWith('no') || l.endsWith('-no'));
  } catch (_) { return false; }
}

async function detectDemoCountry({ urlCountry, prefCountry, currentLang }) {
  // 1. Explicit URL param `?country=` wins (deep links / share)
  if (urlCountry && /^[A-Za-z]{2}$/.test(urlCountry)) return urlCountry.toUpperCase();
  // 2. Saved user setting
  if (prefCountry) return prefCountry.toUpperCase();
  // 3. Strong language → country mapping. We deliberately put this above
  //    IP geolocation: the user's language is a deliberate signal of
  //    *which market they want to demo*, while their IP often reflects
  //    a CDN/datacenter rather than the country they'd find interesting.
  //    For en (no strong country) we fall through to IP.
  try {
    const lang = (currentLang || (navigator.language || '').split('-')[0] || '').toLowerCase();
    if (lang && lang !== 'en' && COUNTRY_FROM_LANG[lang]) {
      return COUNTRY_FROM_LANG[lang];
    }
  } catch (_) { /* SSR-safe */ }
  // 4. IP-based country (cheap network call, 4 s timeout)
  try {
    const r = await axios.get(`${API}/api/geo/detect`, { timeout: 4000 });
    const c = (r?.data?.country || r?.data?.country_code || '').toUpperCase();
    if (c && /^[A-Z]{2}$/.test(c)) return c;
  } catch (_) { /* offline / blocked is fine */ }
  // 5. Final fallback (en + no IP)
  return 'NO';
}

// Leaflet + react-leaflet are heavy. Lazy-load the teaser so the hero can
// paint first and the mini-map hydrates after.
const LiveMapTeaser = lazy(() =>
  import('@/components/LiveMapTeaser').then((m) => ({ default: m.LiveMapTeaser }))
);

const TeaserSkeleton = () => (
  <div
    className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
    data-testid="teaser-suspense-fallback"
  >
    <div className="h-48 bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 animate-pulse" />
    <div className="h-[52px]" />
  </div>
);

const CATEGORIES = [
  { id: 'transport', icon: Truck, to: '/transport', kLabel: 'homepage.popularTransport', cat: 'transport' },
  { id: 'logistics', icon: Truck, to: '/logistics', kLabel: 'homepage.popularLogistics', cat: 'logistics', badge: 'B2B' },
  { id: 'auto', icon: Wrench, to: '/auto-repair', kLabel: 'homepage.popularAuto', cat: 'auto' },
  { id: 'accommodation', icon: HomeIcon, to: '/accommodation', kLabel: 'homepage.popularAccommodation', cat: 'accommodation' },
  { id: 'b2b', icon: Building2, to: '/alltooovott', kLabel: 'homepage.popularB2B', cat: 'b2b' },
  { id: 'rentals', icon: Package, to: '/rentals', kLabel: 'homepage.popularEquipment', cat: 'equipment' },
  { id: 'map', icon: MapPin, to: '/map', kLabel: 'homeV3.catMap', cat: '' },
];

const WHY = [
  { icon: ShieldCheck, kTitle: 'homeV3.why1Title', kDesc: 'homeV3.why1Desc' },
  { icon: MapPin,      kTitle: 'homeV3.why2Title', kDesc: 'homeV3.why2Desc' },
  { icon: CheckCircle, kTitle: 'homeV3.why3Title', kDesc: 'homeV3.why3Desc' },
];

const HOW = [
  { icon: FileText,    kLabel: 'homeV3.how1Compact' },
  { icon: Bell,        kLabel: 'homeV3.how2Compact' },
  { icon: CheckCircle, kLabel: 'homeV3.how3Compact' },
];

export const HomePageV3 = () => {
  const { t, language, setLanguage } = useLanguage();
  const { prefs } = usePrefs();
  const navigate = useNavigate();

  // ---- Live-demo CTA: detect country once on mount, plus the
  // "Bytt til norsk" suggestion when the browser is Norwegian but the
  // user is reading the site in another language.
  const [demoCountry, setDemoCountry] = useState(prefs.serviceCountry || '');
  // Visibility flag: hide the CTA after the user dismisses it OR after
  // they click the button (one-shot behaviour per the spec).
  const [liveDemoVisible, setLiveDemoVisible] = useState(true);
  useEffect(() => {
    try {
      if (localStorage.getItem('keikka_live_demo_cta_dismissed') === 'true') {
        setLiveDemoVisible(false);
      }
    } catch (_) { /* private mode */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const urlCountry = (() => {
        try { return new URLSearchParams(window.location.search).get('country'); }
        catch (_) { return null; }
      })();
      const c = await detectDemoCountry({
        urlCountry,
        prefCountry: prefs.serviceCountry,
        currentLang: language,
      });
      if (!cancelled) setDemoCountry(c);
    })();
    return () => { cancelled = true; };
  }, [prefs.serviceCountry, language]);

  // One-time "Switch to Norwegian" suggestion (toast). Only fires when:
  //  - the browser advertises Norwegian (nb / no / *-NO)
  //  - the user is NOT currently viewing the site in Norwegian
  //  - we haven't already nudged them this session
  useEffect(() => {
    try {
      if (sessionStorage.getItem('keikka_nb_nudge_done') === '1') return;
      if (language === 'nb') return;
      if (!_norwegianBrowser()) return;
      sessionStorage.setItem('keikka_nb_nudge_done', '1');
      toast(t('homeV3.switchToNbToast') || 'Velkommen! Vil du lese på norsk?', {
        action: {
          label: t('homeV3.switchToNbAction') || 'Bytt til norsk',
          onClick: () => {
            try { setLanguage && setLanguage('nb'); } catch (_) { /* noop */ }
            try {
              localStorage.setItem('keikka_lang', 'nb');
              localStorage.setItem('i18nextLng', 'nb');
            } catch (_) { /* noop */ }
            // Lightweight reload to pick up the new locale everywhere.
            setTimeout(() => window.location.reload(), 250);
          },
        },
        duration: 8000,
      });
    } catch (_) { /* SSR / private mode */ }
  }, [language, setLanguage, t]);

  const liveDemoHref = useMemo(() => {
    const lang = _norwegianBrowser() ? 'nb' : (language || 'en');
    const c = (demoCountry || prefs.serviceCountry || 'NO').toUpperCase();
    const params = new URLSearchParams({
      country: c,
      lang,
      to: '/map',
    });
    return `/demo?${params.toString()}`;
  }, [demoCountry, prefs.serviceCountry, language]);

  const mapHref = (() => {
    const p = new URLSearchParams();
    if (prefs.serviceCountry) p.set('country', prefs.serviceCountry);
    if (prefs.locationQuery) p.set('q', prefs.locationQuery);
    const qs = p.toString();
    return qs ? `/map?${qs}` : '/map';
  })();

  // Quick search state — pre-seeds with preferences so returning users land on
  // the same map filters they used last time.
  const [qsCat, setQsCat] = useState('');
  const [qsLoc, setQsLoc] = useState(prefs.locationQuery || '');

  const submitQuickSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (qsCat && qsCat !== '__any__') p.set('subtype', qsCat);
    if (qsLoc.trim()) p.set('q', qsLoc.trim());
    if (prefs.serviceCountry) p.set('country', prefs.serviceCountry);
    navigate(`/map${p.toString() ? '?' + p.toString() : ''}`);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="home-v3">
      <GlobalNav />

      <main className="flex-1">
        {/* Location detected banner — only shown on first visit
            (no prefs set + not recently dismissed) */}
        <LocationDetectedBanner />

        {/* 1. HERO ------------------------------------------------------- */}
        <section className="bg-white">
          <div className="max-w-[1200px] mx-auto px-4 pt-8 pb-6 md:pt-12 md:pb-10">
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-7 space-y-4">
                <div
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold"
                  data-testid="hero-verified-badge"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  {t('homeV3.badgeVerified')}
                </div>
                <h1
                  className="text-4xl sm:text-5xl lg:text-[3.25rem] font-extrabold tracking-tight text-slate-900 leading-[1.05]"
                  data-testid="hero-title"
                >
                  {t('homeV3.heroTitleMapFirst')}
                </h1>
                <p className="text-base sm:text-lg text-slate-600 max-w-xl" data-testid="hero-subtitle">
                  {t('homeV3.heroSubtitleMapFirst')}
                </p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-1">
                  <Link to={mapHref} data-testid="hero-open-map">
                    <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white h-12 px-6 w-full sm:w-auto">
                      <MapPin className="h-5 w-5 mr-2" />
                      {t('homeV3.heroOpenMap')}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/booking" data-testid="hero-request-service">
                    <Button
                      size="lg"
                      variant="outline"
                      className="h-12 px-6 w-full sm:w-auto border-slate-300 text-slate-800 hover:bg-slate-50"
                    >
                      {t('homeV3.ctaRequestService')}
                    </Button>
                  </Link>
                  {/* "Live demo" — opens a country-aware demo deep link in a
                      new tab so the user keeps the home page open. Hidden
                      after the user dismisses it (× button) or clicks
                      through (one-shot first-time-visitor pattern). */}
                  {liveDemoVisible && (
                    <div
                      className="relative w-full sm:w-auto"
                      data-testid="live-demo-block"
                    >
                      <a
                        href={liveDemoHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-live-demo"
                        aria-label={t('homeV3.ctaLiveDemo') || 'Live demo'}
                        onClick={() => {
                          // 1. dismiss + remember
                          try {
                            localStorage.setItem('keikka_live_demo_cta_dismissed', 'true');
                          } catch (_) { /* private mode */ }
                          // 2. fire the two telemetry events
                          try {
                            trackMapEvent('live_demo_cta_click', {
                              label: `${language || 'en'}|${demoCountry || ''}`,
                            });
                            trackMapEvent('live_demo_opened', {
                              label: `${language || 'en'}|${demoCountry || ''}`,
                            });
                          } catch (_) { /* analytics never blocks UI */ }
                          // 3. hide for the rest of the session
                          setLiveDemoVisible(false);
                        }}
                      >
                        <Button
                          size="lg"
                          variant="ghost"
                          className="h-12 px-5 w-full sm:w-auto text-orange-700 hover:text-orange-800 hover:bg-orange-50"
                        >
                          <PlayCircle className="h-5 w-5 mr-2" />
                          {t('homeV3.ctaLiveDemo') || 'Live demo'}
                        </Button>
                      </a>
                      <button
                        type="button"
                        aria-label={t('homeV3.dismissLiveDemo') || 'Dismiss'}
                        data-testid="hero-live-demo-dismiss"
                        onClick={() => {
                          try {
                            localStorage.setItem('keikka_live_demo_cta_dismissed', 'true');
                          } catch (_) { /* noop */ }
                          setLiveDemoVisible(false);
                        }}
                        className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-white border border-slate-300 text-slate-500 hover:text-slate-700 text-xs leading-none flex items-center justify-center shadow-sm"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                {liveDemoVisible && (
                  <p
                    className="text-xs text-slate-500 mt-1"
                    data-testid="hero-live-demo-subtext"
                  >
                    {t('homeV3.ctaLiveDemoSub') || 'One click → see your country live'}
                  </p>
                )}
              </div>

              {/* Live map teaser with real pins (desktop only for hero row;
                  mobile gets a full-width teaser block below) */}
              <aside className="hidden md:block md:col-span-5">
                <Suspense fallback={<TeaserSkeleton />}>
                  <LiveMapTeaser
                    country={prefs.serviceCountry || ''}
                    locationQuery={prefs.locationQuery || ''}
                  />
                </Suspense>
              </aside>
            </div>

            {/* Mobile-only teaser: stacked below hero, above quick search */}
            <div className="md:hidden mt-5" data-testid="mobile-teaser-wrap">
              <Suspense fallback={<TeaserSkeleton />}>
                <LiveMapTeaser
                  country={prefs.serviceCountry || ''}
                  locationQuery={prefs.locationQuery || ''}
                />
              </Suspense>
            </div>
          </div>
        </section>

        {/* 2. QUICK SEARCH BAR ------------------------------------------- */}
        <section className="bg-slate-50 border-y border-slate-100">
          <div className="max-w-[1200px] mx-auto px-4 py-5 md:py-6">
            <form
              onSubmit={submitQuickSearch}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 md:p-4"
              data-testid="quick-search-form"
            >
              <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
                {t('homeV3.quickSearchTitle')}
              </div>
              <div className="grid md:grid-cols-12 gap-2">
                <div className="md:col-span-4">
                  <Select value={qsCat} onValueChange={setQsCat}>
                    <SelectTrigger className="h-11" data-testid="quick-search-category">
                      <SelectValue placeholder={t('homeV3.quickSearchAnyCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__any__">{t('homeV3.quickSearchAnyCategory')}</SelectItem>
                      {CATEGORIES.filter((c) => c.cat).map((c) => (
                        <SelectItem key={c.id} value={c.cat}>
                          {t(c.kLabel)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-5">
                  <Input
                    value={qsLoc}
                    onChange={(e) => setQsLoc(e.target.value)}
                    placeholder={t('homeV3.quickSearchLocation')}
                    className="h-11"
                    data-testid="quick-search-location"
                  />
                </div>
                <div className="md:col-span-3">
                  <Button
                    type="submit"
                    className="h-11 w-full bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="quick-search-submit"
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    {t('homeV3.quickSearchShowMap')}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </section>

        {/* 3. CATEGORY STRIP -------------------------------------------- */}
        <section className="bg-white py-8 md:py-10">
          <div className="max-w-[1200px] mx-auto px-4">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-lg md:text-xl font-bold text-slate-900">
                {t('homeV3.categoriesStripTitle')}
              </h2>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 md:gap-3">
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                // All cards now share the unified slate/violet style. The
                // optional `badge` prop (e.g. "B2B") is restyled to match
                // the same violet design language — no orange emphasis.
                return (
                  <Link
                    key={c.id}
                    to={c.to}
                    data-testid={`category-${c.id}`}
                    className="group relative bg-white rounded-xl border border-slate-200 hover:border-violet-300 p-3 flex flex-col items-center gap-1.5 text-center hover:shadow-sm transition"
                  >
                    {c.badge && (
                      <span
                        className="absolute -top-2 -right-2 inline-flex items-center px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200 text-[9px] font-bold uppercase tracking-wide"
                        data-testid={`category-${c.id}-badge`}
                      >
                        {c.badge}
                      </span>
                    )}
                    <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 group-hover:bg-violet-600 group-hover:text-white flex items-center justify-center transition-colors">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="text-xs md:text-sm font-medium text-slate-800 leading-tight">
                      {t(c.kLabel)}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* 4. WHY KEIKKA ------------------------------------------------ */}
        <section className="bg-slate-50 border-t border-slate-100 py-10 md:py-12">
          <div className="max-w-[1200px] mx-auto px-4">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6" data-testid="why-title">
              {t('homeV3.whyTitle')}
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {WHY.map((w) => {
                const Icon = w.icon;
                return (
                  <div
                    key={w.kTitle}
                    className="bg-white rounded-2xl border border-slate-200 p-5"
                    data-testid={`why-${w.kTitle}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-3">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="text-base font-semibold text-slate-900">
                      {t(w.kTitle)}
                    </div>
                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                      {t(w.kDesc)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* 5. COMPACT HOW IT WORKS -------------------------------------- */}
        <section className="bg-white py-10 md:py-12">
          <div className="max-w-[1200px] mx-auto px-4">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 text-center">
              {t('homeV3.howCompactTitle')}
            </h2>
            <ol className="grid md:grid-cols-3 gap-3 md:gap-4 list-none">
              {HOW.map((s, i) => {
                const Icon = s.icon;
                return (
                  <li
                    key={s.kLabel}
                    className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3"
                  >
                    <div className="relative flex-shrink-0">
                      <div className="w-10 h-10 rounded-lg bg-orange-500 text-white flex items-center justify-center">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                    </div>
                    <div className="text-sm md:text-[15px] text-slate-800 leading-snug pt-1.5">
                      {t(s.kLabel)}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </section>

        {/* 6. PROVIDER SHORT CTA ---------------------------------------- */}
        <section className="bg-white py-10 md:py-12 border-t border-slate-100">
          <div className="max-w-[1200px] mx-auto px-4">
            <div className="bg-orange-50 border border-orange-100 rounded-2xl px-6 py-6 md:px-8 md:py-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="space-y-1">
                <h2 className="text-xl md:text-2xl font-bold text-slate-900">
                  {t('homeV3.providerShortTitle')}
                </h2>
                <p className="text-slate-600 text-sm md:text-base">
                  {t('homeV3.providerShortSub')}
                </p>
              </div>
              <Link to="/provider/register" className="md:flex-shrink-0" data-testid="provider-short-cta">
                <Button
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white h-12 px-7 w-full md:w-auto"
                >
                  {t('homeV3.providerShortCta')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="py-4 bg-white border-t border-slate-200">
        <div className="max-w-[1200px] mx-auto px-4 text-center text-slate-500 text-xs">
          © 2026 KEIKKA. {t('homepage.footerRights')}
        </div>
      </footer>
    </div>
  );
};

export default HomePageV3;
